import type { FaqCategory } from './types/faq.types';

/**
 * FAQ copy, transcribed verbatim from the client-supplied `11-FAQs Page content` document.
 *
 * This REPLACES the earlier prototype copy (six invented questions from the approved mockup).
 *
 * The document groups its copy under three headings, which become the three accordion categories. The
 * middle one is not written as questions — it is a heading with three sub-sections — so each
 * sub-section heading becomes the accordion trigger rather than inventing questions for them.
 *
 * The wording is the client's and is reproduced exactly, typos included. Reported rather than silently
 * corrected, for Bankers Fidelity to fix at source:
 *   - "…not accepted for Medicare Supplement claims" — no closing full stop.
 *   - "CMS offers free software to help provider view and print Medicare RAs" — reads as "providers".
 *
 * The "Update Medicare Supplement Claims FAQ Language Regarding Medicare Processing Documentation"
 * work item renamed the third category (formerly "… – Remittance Advice Requirements") and replaced
 * its first question with the Medicare Processing Documentation one, transcribed verbatim from that
 * ticket; the remaining four questions are unchanged. The replaced answer carried the reported
 * "what are essential" typo, which left with it.
 *
 * Three claims in this copy match Won't-v1 scope decisions in the backlog, so they are consistent
 * rather than contradictory: no email submission (W-001), no fax submission (W-002), and no summary RA
 * with multiple patients whether highlighted or redacted (W-003).
 */

const CLAIMS_ADDRESS = [
  'Bankers Fidelity Life Insurance Company',
  'PO Box 211701',
  'Eagan, MN 55121',
];

export const FAQ_CATEGORIES: readonly FaqCategory[] = [
  {
    name: 'Medicare Supplement Claims',
    items: [
      {
        question:
          'Is pre-certification required for hospitalization or other services under the Medicare Supplement policies with Bankers Fidelity?',
        answer: [
          {
            kind: 'paragraph',
            content: [
              'Pre-certification is not typically required for a Medicare Supplement policy. If you require additional information for a specific service, please refer to plan information available on our provider portal.',
            ],
          },
        ],
      },
      {
        question:
          'What is the timely filing limit for Medicare Supplement claims with Bankers Fidelity?',
        answer: [
          {
            kind: 'paragraph',
            content: [
              'The timely filing for Medicare Supplement claims with Bankers Fidelity is ',
              { bold: '15 months' },
              ' from the date of service.',
            ],
          },
        ],
      },
      {
        question: 'What do your Medicare Supplement plans cover?',
        answer: [
          {
            kind: 'paragraph',
            content: [
              'Bankers Fidelity Medicare Supplement plans, also known as Medigap policies, are designed to help fill the "gaps" in Original Medicare coverage. These plans cover various out-of-pocket costs that Original Medicare does not, such as copayments, coinsurance, and deductibles. ',
              { bold: 'The specific benefits depend on the plan selected.' },
              ' For detailed information regarding coverage for a particular policyholder, please refer to the policy documents on the provider portal.',
            ],
          },
        ],
      },
      {
        question: 'How do I send Medicare Supplement claims to Bankers Fidelity for processing?',
        answer: [
          {
            kind: 'paragraph',
            content: [
              'To send Medicare Supplement claims to Bankers Fidelity for processing, please mail them to:',
            ],
          },
          { kind: 'address', lines: CLAIMS_ADDRESS },
        ],
      },
      {
        question: 'Can I email Medicare Supplement claims to Bankers Fidelity?',
        answer: [
          {
            kind: 'paragraph',
            content: [
              'No, email is not an accepted method for submitting Medicare Supplement claims to Bankers Fidelity.',
            ],
          },
        ],
      },
      {
        question: 'Can I fax Medicare Supplement claims to Bankers Fidelity?',
        answer: [
          {
            kind: 'paragraph',
            content: ['No, fax submissions are not accepted for Medicare Supplement claims'],
          },
        ],
      },
      {
        question: 'Why doesn’t Bankers Fidelity accept claims by email or fax?',
        answer: [
          {
            kind: 'paragraph',
            content: [
              'To ensure ',
              { bold: 'secure, accurate,' },
              ' and ',
              { bold: 'confidential' },
              ' handling of claims, ',
              { bold: 'Bankers Fidelity only accepts submissions by mail.' },
              ' This helps to:',
            ],
          },
          {
            kind: 'list',
            items: [
              ['Protect sensitive patient information'],
              ['Ensure document quality and completeness'],
              ['Maintain consistent intake and processing standards.'],
            ],
          },
          {
            kind: 'paragraph',
            content: [
              'For these reasons, please submit all Medicare Supplement claims by mail to:',
            ],
          },
          { kind: 'address', lines: CLAIMS_ADDRESS },
          {
            kind: 'paragraph',
            content: [
              'We appreciate your understanding and cooperation in helping us maintain secure and efficient claims processing.',
            ],
          },
        ],
      },
    ],
  },
  {
    name: 'Submitting Initial, Corrected, and Rejected Claims',
    items: [
      {
        question: 'Initial Claims',
        answer: [
          { kind: 'paragraph', content: ['To avoid delays, please include:'] },
          {
            kind: 'list',
            items: [
              ['A completed claim form (if applicable)'],
              ['The Medicare Single Claim Remittance Advice (RA)'],
              ['Any relevant supporting medical documentation (if applicable)'],
              ['Policyholder details: full name, policy number, date of birth, and contact info'],
            ],
          },
        ],
      },
      {
        question: 'Corrected Claims',
        answer: [
          {
            kind: 'paragraph',
            content: [
              'If a claim was previously ',
              { bold: 'processed and adjudicated' },
              ', but an error was found (e.g., incorrect diagnosis code, provider info, or billing detail):',
            ],
          },
          {
            kind: 'list',
            items: [
              ['Clearly mark the submission as ', { bold: '“Corrected Claim”' }],
              ['Include a brief explanation of the correction'],
              ['Attach a copy of the original claim and any correspondence from Bankers Fidelity'],
            ],
          },
        ],
      },
      {
        question: 'Resubmissions for Rejected Claims (Not Adjudicated)',
        answer: [
          {
            kind: 'paragraph',
            content: [
              'If a claim was ',
              { bold: 'rejected before adjudication' },
              ' (e.g., due to missing documents or formatting issues):',
            ],
          },
          {
            kind: 'list',
            items: [
              [{ bold: 'Do NOT mark it as a corrected claim' }],
              ['Instead, ', { bold: 'resubmit it as a new/initial claim' }],
              ['Include all required documents as if submitting for the first time'],
              [
                'Optionally, include a cover letter referencing the previous rejection and explaining that this is ',
                { bold: 'a resubmission of a previously rejected claim' },
              ],
            ],
          },
          {
            kind: 'paragraph',
            content: [
              'This ensures the claim enters the system correctly and is processed without confusion.',
            ],
          },
        ],
      },
    ],
  },
  {
    name: 'Medicare Supplement Claims – Medicare Processing Documentation',
    items: [
      {
        question: 'What is required to process a Medicare Supplement claim?',
        answer: [
          {
            kind: 'paragraph',
            content: [
              'To process a Medicare Supplement claim, we require documentation showing that Medicare has processed the claim. The documentation must include sufficient claim-level information to identify the claim and determine the supplemental benefit amount, such as an ICN, dates of service, Medicare payment information, deductible, coinsurance, copayment, and any applicable adjustments. If the documentation does not contain enough information to identify the claim or calculate benefits, additional information may be requested.',
            ],
          },
        ],
      },
      {
        question: 'Why is a summary RA with multiple patients not acceptable?',
        answer: [
          {
            kind: 'paragraph',
            content: [
              'Summary RAs do not provide the claim-level detail necessary for accurate processing. They often omit specific information about individual patients and services, which can lead to errors or delays.',
            ],
          },
        ],
      },
      {
        question: 'What happens if a provider submits a summary RA or incorrect documentation?',
        answer: [
          {
            kind: 'paragraph',
            content: ['Submitting incorrect or incomplete documentation may result in:'],
          },
          {
            kind: 'list',
            items: [
              ['Claim denial or delay'],
              ['Requests for additional information'],
              [
                'Suspension of payment processing until the correct RA or documentation is received',
              ],
            ],
          },
        ],
      },
      {
        question:
          'Can I submit a remittance advice that includes multiple patients if I highlight the relevant claim or redact the patients not included in the claim?',
        answer: [
          {
            kind: 'paragraph',
            content: [
              'No. Submitting remittance advice (RA) that includes multiple patients—even if you highlight the relevant claim or redact the others—is not acceptable for Medicare Supplement claim processing.',
            ],
          },
          {
            kind: 'list',
            items: [
              ['Highlighting a claim does not meet CMS documentation standards.'],
              [
                'Redacting other patients may interfere with the document’s completeness and traceability.',
              ],
            ],
          },
          {
            kind: 'paragraph',
            content: [
              'We require the original, unmodified RA that clearly shows the full adjudication details for the individual patient and claim.',
            ],
          },
        ],
      },
      {
        question: 'What if I cannot locate the correct RA?',
        answer: [
          {
            kind: 'paragraph',
            content: [
              'If the correct Medicare RA is not available, providers should take the following steps to obtain the required documentation for Medicare Supplement claim processing:',
            ],
          },
          { kind: 'subheading', text: 'Request a duplicate RA from their MAC' },
          {
            kind: 'paragraph',
            content: ['CMS offers free software to help provider view and print Medicare RAs:'],
          },
          {
            kind: 'subheading',
            text: 'Medicare Remit Easy Print (MREP) – for professional providers and suppliers',
          },
          {
            kind: 'list',
            items: [
              ['Allows users to view, print, and manage HIPAA-compliant 835 ERA files.'],
              [
                'Generates claim-specific reports in a format similar to the Standard Paper Remittance (SPR).',
              ],
              [
                'Available for download from the ',
                {
                  link: 'CMS Medicare Remit Easy Print (MREP) Page',
                  href: 'https://www.cms.gov/data-research/cms-information-technology/access-cms-data-application/medicare-remit-easy-print',
                },
              ],
            ],
          },
          { kind: 'subheading', text: 'PC-Print – for institutional providers' },
          {
            kind: 'list',
            items: [
              [
                'Enables institutional providers to view and print remittance advice from ERA files.',
              ],
              ['Eliminates the need to wait for mailed SPRs.'],
              [
                { bold: 'Available through your MAC' },
                ' ',
                {
                  link: 'Medicare Claims Processing Manual, Chapter 22 Remittance Advice, Section 40.4 Medicare Standard Electronic PC-Print Software for Institutional Providers',
                  href: 'https://www.cms.gov/regulations-and-guidance/guidance/manuals/downloads/clm104c22pdf.pdf',
                },
              ],
            ],
          },
          {
            kind: 'paragraph',
            content: [
              {
                bold: 'Access their Medicare Administrative Contractor (MAC) portal to download the claim specific RA or the PC Print Software',
              },
              '. ',
              {
                link: 'A/B MACs and HH+H Areas',
                href: 'https://www.cms.gov/medicare/coding-billing/medicare-administrative-contractors-macs/who-are-macs',
              },
            ],
          },
        ],
      },
    ],
  },
];
